import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BarChart3,
  BatteryCharging,
  Bell,
  Boxes,
  CircleHelp,
  ClipboardList,
  FlaskConical,
  Gauge,
  Grid3X3,
  Languages,
  LogOut,
  Microscope,
  PackageCheck,
  ScanBarcode,
  Settings,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n';
import type { Language, TranslationKey } from '../../i18n';

interface LayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  to?: string;
  labelKey: TranslationKey;
  icon: LucideIcon;
  match?: string[];
  disabled?: boolean;
}

const primaryNav: NavigationItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: Grid3X3 },
  { labelKey: 'nav.liveTesting', icon: Microscope, disabled: true },
  { to: '/battery-models', labelKey: 'nav.batchMgmt', icon: Boxes },
  { labelKey: 'nav.labTools', icon: FlaskConical, disabled: true },
  { labelKey: 'nav.disposition', icon: PackageCheck, disabled: true },
  { labelKey: 'nav.analytics', icon: BarChart3, disabled: true },
  { labelKey: 'nav.admin', icon: ShieldCheck, disabled: true },
];

const topNav: NavigationItem[] = [
  { to: '/dashboard', labelKey: 'nav.overview', icon: Gauge },
  { labelKey: 'nav.testing', icon: Microscope, disabled: true },
  { to: '/battery-models', labelKey: 'nav.lots', icon: Boxes },
  { labelKey: 'nav.equipment', icon: BatteryCharging, disabled: true },
  { to: '/battery-tests', labelKey: 'nav.reports', icon: ClipboardList },
];

function isActive(pathname: string, item: NavigationItem): boolean {
  if (!item.to || item.disabled) return false;
  if (item.to === pathname) return true;
  return item.match?.some((path) => pathname.startsWith(path)) ?? false;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const { language, languages, languageLabels, setLanguage, t } = useI18n();
  const { pathname } = useLocation();
  const pageTitle =
    pathname === '/battery-tests'
      ? t('pageTitle.batteryTests')
      : pathname === '/battery-models'
        ? t('pageTitle.batteryModels')
        : t('pageTitle.dashboard');

  return (
    <div className="min-h-screen bg-console-base text-console-text">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r border-console-border bg-console-panel/95 xl:flex">
        <div className="border-b border-console-border p-6">
          <h1 className="text-3xl font-bold leading-none text-console-blueSoft">ConnectDevice</h1>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-console-muted">{t('app.shortPlatform')}</p>
        </div>

        <div className="m-4 rounded border border-console-blueSoft/50 bg-console-panelHigh p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-console-blue text-white">
              <UserRound size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-console-text">{t('app.demoClient')}</p>
              <p className="text-xs font-semibold text-console-muted">{t('app.localDevelopment')}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-4 py-5">
          {primaryNav.map((item) => {
            const Icon = item.icon;
            const active = isActive(pathname, item);
            const label = t(item.labelKey);

            if (item.disabled) {
              return (
                <button
                  key={item.labelKey}
                  type="button"
                  aria-disabled="true"
                  disabled
                  className="flex w-full cursor-not-allowed items-center gap-3 rounded px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-console-muted/45 opacity-75 transition hover:bg-transparent hover:text-console-muted/45"
                  title={t('app.notAvailableYet', { label })}
                >
                  <Icon size={20} strokeWidth={1.8} />
                  {label}
                </button>
              );
            }

            return (
              <NavLink
                key={item.labelKey}
                to={item.to ?? '/dashboard'}
                className={`flex items-center gap-3 rounded px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] transition ${
                  active
                    ? 'bg-console-blueSoft/20 text-console-blueSoft ring-1 ring-console-blueSoft/20'
                    : 'text-console-muted hover:bg-console-panelHigh hover:text-console-text'
                }`}
              >
                <Icon size={20} strokeWidth={1.8} />
                {label}
              </NavLink>
            );
          })}
        </nav>

        <div className="space-y-4 border-t border-console-border p-4">
          <button
            type="button"
            aria-disabled="true"
            disabled
            className="console-button w-full cursor-not-allowed border-console-blueSoft/40 bg-console-blueSoft/25 text-console-blueSoft/60 opacity-80 hover:border-console-blueSoft/40 hover:bg-console-blueSoft/25 hover:text-console-blueSoft/60"
            title={t('app.notAvailableYet', { label: t('nav.scanNewSerial') })}
          >
            <ScanBarcode size={18} />
            {t('nav.scanNewSerial')}
          </button>
          <div className="space-y-1">
            <button
              type="button"
              aria-disabled="true"
              disabled
              className="flex w-full cursor-not-allowed items-center gap-3 rounded px-4 py-2 text-left text-xs font-bold uppercase tracking-[0.08em] text-console-muted/45 opacity-75"
              title={t('app.notAvailableYet', { label: t('nav.support') })}
            >
              <CircleHelp size={18} />
              {t('nav.support')}
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="flex w-full items-center gap-3 rounded px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-console-muted hover:text-console-text"
            >
              <LogOut size={18} />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </aside>

      <div className="xl:pl-72">
        <header className="sticky top-0 z-20 border-b border-console-border bg-console-base/95 backdrop-blur-xl">
          <div className="flex min-h-20 items-center justify-between gap-5 px-5 sm:px-8">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-console-muted xl:hidden">ConnectDevice</p>
              <h2 className="truncate text-2xl font-bold text-console-blueSoft">{pageTitle}</h2>
            </div>

            <nav className="hidden items-center gap-2 lg:flex">
              {topNav.map((item) => {
                const active = isActive(pathname, item);

                if (item.disabled) {
                  return (
                    <button
                      key={item.labelKey}
                      type="button"
                      aria-disabled="true"
                      disabled
                      className="cursor-not-allowed border-b-2 border-transparent px-3 py-2 text-sm font-semibold text-console-muted/45 opacity-75"
                      title={t('app.notAvailableYet', { label: t(item.labelKey) })}
                    >
                      {t(item.labelKey)}
                    </button>
                  );
                }

                return (
                  <NavLink
                    key={item.labelKey}
                    to={item.to ?? '/dashboard'}
                    className={`border-b-2 px-3 py-2 text-sm font-semibold transition ${
                      active
                        ? 'border-console-blueSoft text-console-blueSoft'
                        : 'border-transparent text-console-text/80 hover:text-console-blueSoft'
                    }`}
                  >
                    {t(item.labelKey)}
                  </NavLink>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 rounded border border-console-border bg-console-panelHigh px-2 py-1.5 text-console-muted">
                <Languages size={16} />
                <span className="sr-only">{t('language.label')}</span>
                <select
                  aria-label={t('language.label')}
                  className="bg-transparent text-xs font-bold uppercase text-console-text outline-none"
                  value={language}
                  onChange={(event) => setLanguage(event.target.value as Language)}
                >
                  {languages.map((option) => (
                    <option key={option} value={option}>
                      {t(languageLabels[option])}
                    </option>
                  ))}
                </select>
              </label>
              <button className="relative rounded p-2 text-console-text/80 hover:bg-console-panelHigh hover:text-console-blueSoft" type="button" aria-label={t('nav.notifications')}>
                <Bell size={20} />
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-300" />
              </button>
              <button className="rounded p-2 text-console-text/80 hover:bg-console-panelHigh hover:text-console-blueSoft" type="button" aria-label={t('nav.settings')}>
                <Settings size={20} />
              </button>
              <div className="hidden items-center gap-3 border-l border-console-border pl-4 sm:flex">
                <div className="text-right">
                  <p className="text-xs font-bold uppercase tracking-[0.08em] text-console-blueSoft">{user?.role ?? t('auth.operatorFallback')}</p>
                  <p className="text-xs text-console-muted">{user?.username ?? t('app.localDevelopment')}</p>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded border border-console-blueSoft/50 bg-console-panelHigh text-console-blueSoft">
                  <UserRound size={18} />
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="px-5 py-6 sm:px-8">
          {children}
        </main>
      </div>
    </div>
  );
}
